import { useMemo, useState } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	ComboboxTrigger,
} from '../combobox';
import { cn } from '../../lib/utils';

type Option = { value: string; label: string };

export default function SelectTextField({
	label,
	options,
	className,
	orientation,
	optional,
}: {
	label: string;
	options: Option[];
	className?: string;
	orientation?: 'vertical' | 'horizontal';
	optional?: boolean;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isHorizontal = orientation === 'horizontal';
	const isOptional = useIsFieldOptional(field.name, optional);
	const [searchValue, setSearchValue] = useState('');

	const selectedItem = useMemo(
		() => options.find((i) => i.value === field.state.value) ?? null,
		[options, field.state.value],
	);

	const filteredItems = useMemo(() => {
		if (!searchValue) return options;
		const search = searchValue.toLowerCase();
		return options.filter((item) => item.label.toLowerCase().includes(search));
	}, [options, searchValue]);

	return (
		<div
			className={cn(
				'flex flex-col gap-2',
				className,
				'[&_button]:transition-[color,border-color,padding,box-shadow]',
				formReadOnly &&
					'pointer-events-none [&_button]:border-transparent [&_button]:px-0 [&_button]:focus-visible:ring-0 [&_[data-slot=combobox-trigger]>svg]:hidden',
			)}
		>
			<div className={isHorizontal ? 'flex items-start gap-3' : 'grid gap-2'}>
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						className={cn('flex items-center justify-between', isHorizontal && 'w-36 shrink-0')}
					/>
				)}
				<div className={cn(isHorizontal ? 'flex-1' : undefined)}>
					<Combobox
						items={options}
						value={selectedItem}
						onValueChange={(item: Option | null) => field.handleChange(item?.value ?? '')}
						onInputValueChange={setSearchValue}
						isItemEqualToValue={(a: Option, b: Option) => a.value === b.value}
					>
						<ComboboxTrigger
							render={
								<button
									id={field.name}
									type="button"
									aria-invalid={hasFieldError(field.state.meta)}
									className={cn(
										'dark:bg-input/30 border-input focus-visible:border-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive aria-invalid:ring-3 flex h-8 w-full items-center justify-between rounded-lg border bg-transparent px-2.5 text-sm transition-[color,border-color,padding,box-shadow] outline-none',
										!selectedItem && 'text-muted-foreground',
									)}
								/>
							}
						>
							{selectedItem ? <span>{selectedItem.label}</span> : <span />}
						</ComboboxTrigger>
						<ComboboxContent>
							<ComboboxInput showTrigger={false} placeholder="Search..." />
							<ComboboxList>
								{filteredItems.map((item) => (
									<ComboboxItem key={item.value} value={item}>
										{item.label}
									</ComboboxItem>
								))}
							</ComboboxList>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
						</ComboboxContent>
					</Combobox>
					<FieldInfo fieldState={field.state} />
				</div>
			</div>
		</div>
	);
}
